/**
 * useProjectsData Hook
 *
 * Main hook for loading projects data via API (no IndexedDB caching).
 * Keeps Redux in sync with the latest response.
 */

import { useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';

import { useTypedDispatch } from 'store';

import { PROJECTS_DATA_QUERY_KEYS } from './projectsDataQueryKeys';

import { fetchAllProjectsWithPairs, FetchProjectsResult } from '../api/fetchAllProjectsWithPairs';
import { syncProjectsToRedux } from '../lib/syncToRedux';

interface UseProjectsDataOptions {
  /** Whether to enable data fetching (default: true) */
  enabled?: boolean;
}

interface UseProjectsDataReturn {
  /** Whether initial loading is in progress */
  isLoading: boolean;
  /** Whether background refetch is in progress */
  isFetching: boolean;
  /** Whether data is stale in React Query cache */
  isStale: boolean;
  /** Error from the last fetch attempt */
  error: Error | null;
  /** Timestamp of last successful data update */
  lastUpdated: number | null;
  /** Manually trigger a refetch */
  refetch: () => Promise<void>;
}

/**
 * Hook for loading projects data
 *
 * @param options - Configuration options
 * @returns Loading state and control methods
 */
export const useProjectsData = (options: UseProjectsDataOptions = {}): UseProjectsDataReturn => {
  const { enabled = true } = options;

  const dispatch = useTypedDispatch();

  const query = useQuery({
    queryKey: PROJECTS_DATA_QUERY_KEYS.list(),
    queryFn: async (): Promise<FetchProjectsResult> => {
      const result = await fetchAllProjectsWithPairs();
      syncProjectsToRedux(dispatch, result);
      return result;
    },
    enabled,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    retry: 2,
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  const lastUpdated = query.dataUpdatedAt > 0 ? query.dataUpdatedAt : null;

  const refetch = useCallback(async () => {
    await query.refetch();
  }, [query]);

  return {
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isStale: query.isStale,
    error: query.error as Error | null,
    lastUpdated,
    refetch,
  };
};
